import type { CSSProperties } from 'react';
import { Button, Select } from 'antd';
import { BulbFilled, BulbOutlined, CloseOutlined } from '@ant-design/icons';
import type { MapBasemapThemeOverride } from './useNowcastingState';

type MapBackgroundSwatchKind = 'default' | 'satellite' | 'terrain';

function MapBackgroundSwatch({
  kind,
  theme,
  effectiveMapBasemapTheme,
}: {
  kind: MapBackgroundSwatchKind;
  theme: 'dark' | 'light';
  effectiveMapBasemapTheme: 'dark' | 'light';
}) {
  const border =
    theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.12)';
  const common: CSSProperties = {
    width: 36,
    height: 24,
    borderRadius: 4,
    flexShrink: 0,
    alignSelf: 'center',
    border,
    boxSizing: 'border-box',
    display: 'block',
  };

  if (kind === 'default') {
    return (
      <span
        style={{
          ...common,
          backgroundColor: effectiveMapBasemapTheme === 'dark' ? '#1a1a1a' : '#f5f5f5',
        }}
      />
    );
  }

  if (kind === 'satellite') {
    return (
      <span
        style={{
          ...common,
          backgroundColor: '#4a6b3a',
          backgroundImage:
            'linear-gradient(45deg, #4a6b3a 25%, #5a7b4a 25%, #5a7b4a 50%, #4a6b3a 50%, #4a6b3a 75%, #5a7b4a 75%, #5a7b4a)',
          backgroundSize: '6px 6px',
        }}
      />
    );
  }

  return (
    <span
      style={{
        ...common,
        backgroundColor: '#d4c4a8',
        backgroundImage: 'linear-gradient(180deg, #d4c4a8 0%, #a89478 100%)',
      }}
    />
  );
}

function mapStyleKeyToSwatchKind(key: string): MapBackgroundSwatchKind {
  return key === 'satellite' ? 'satellite' : 'terrain';
}

type MapThemeSelectValue = 'match' | 'light' | 'dark';

/** Bulb icons: light = outline; dark / night = filled with a dark silhouette (not yellow). */
function MapThemeOptionIcon({
  kind,
  appTheme,
}: {
  kind: MapThemeSelectValue;
  appTheme: 'dark' | 'light';
}) {
  const box: CSSProperties = {
    width: 36,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    lineHeight: 0,
  };
  const lineColor = appTheme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)';
  // Filled bulb for “dark” must stay visible on both light and dark popups
  const darkBulbFill = appTheme === 'dark' ? '#8c8c8c' : '#262626';

  if (kind === 'light') {
    return (
      <span style={box}>
        <BulbOutlined style={{ fontSize: 16, color: lineColor }} />
      </span>
    );
  }
  if (kind === 'dark') {
    return (
      <span style={box}>
        <BulbFilled style={{ fontSize: 16, color: darkBulbFill }} />
      </span>
    );
  }
  // Same as app — outline when app is light; dark-filled bulb when app is dark
  return (
    <span style={box}>
      {appTheme === 'dark' ? (
        <BulbFilled style={{ fontSize: 16, color: darkBulbFill }} />
      ) : (
        <BulbOutlined style={{ fontSize: 16, color: lineColor }} />
      )}
    </span>
  );
}

interface MapStyle {
  name: string;
  url: string;
}

interface MapSettingsPanelProps {
  isVisible: boolean;
  onClose: () => void;
  theme: 'dark' | 'light';
  mapBasemapThemeOverride: MapBasemapThemeOverride;
  onMapBasemapThemeOverrideChange: (value: MapBasemapThemeOverride) => void;
  mapStyle: string | null;
  mapStyles: Record<string, MapStyle>;
  onMapStyleChange: (newStyle: string | null) => void;
  observationsEnabled: boolean;
  showWindArrows: boolean;
  onWindArrowsToggle: (value: boolean) => void;
  // Storm tracking (feature toggle: only visible to root users during development)
  stormTrackingFeatureEnabled: boolean;
  showStormTracking: boolean;
  onStormTrackingToggle: (value: boolean) => void;
  // Rain path (feature toggle: only visible to root users during development)
  showRainPath: boolean;
  onRainPathToggle: (value: boolean) => void;
  // Track line width
  trackLineWidth: number;
  onTrackLineWidthChange: (value: number) => void;
  // Radius circles settings
  showRadiusCircles: boolean;
  onRadiusCirclesToggle: (value: boolean) => void;
  radiusCircleCount: number;
  onRadiusCircleCountChange: (value: number) => void;
  radiusCircleDistance: number;
  onRadiusCircleDistanceChange: (value: number) => void;
}

export const MapSettingsPanel = ({
  isVisible,
  onClose,
  theme,
  mapBasemapThemeOverride,
  onMapBasemapThemeOverrideChange,
  mapStyle,
  mapStyles,
  onMapStyleChange,
  observationsEnabled,
  showWindArrows,
  onWindArrowsToggle,
  stormTrackingFeatureEnabled,
  showStormTracking,
  onStormTrackingToggle,
  showRainPath,
  onRainPathToggle,
  trackLineWidth,
  onTrackLineWidthChange,
  showRadiusCircles,
  onRadiusCirclesToggle,
  radiusCircleCount,
  onRadiusCircleCountChange,
  radiusCircleDistance,
  onRadiusCircleDistanceChange,
}: MapSettingsPanelProps) => {
  if (!isVisible) return null;

  const effectiveMapBasemapTheme = mapBasemapThemeOverride ?? theme;

  const mapBackgroundSelectValue = mapStyle === null ? 'default' : mapStyle;
  const mapBackgroundSwatchKind: MapBackgroundSwatchKind =
    mapStyle === null ? 'default' : mapStyleKeyToSwatchKind(mapStyle);

  const mapThemeSelectValue: MapThemeSelectValue =
    mapBasemapThemeOverride === null ? 'match' : mapBasemapThemeOverride;

  return (
    <div style={{
      position: 'absolute',
      top: 24,
      right: 70,
      backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
      color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
      padding: '16px',
      borderRadius: 12,
      zIndex: 1001,
      minWidth: 240,
      maxHeight: 'calc(100% - 48px)',
      display: 'flex',
      flexDirection: 'column',
      border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      backdropFilter: 'blur(10px)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
        paddingBottom: 12,
        borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)'}`,
      }}>
        <div style={{
          fontSize: 16,
          fontWeight: 600,
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
        }}>
          Map settings
        </div>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
          style={{
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
            minWidth: 24,
            padding: '0 4px',
          }}
        />
      </div>

      {/* Content */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        <div style={{
          fontSize: 14,
          fontWeight: 600,
          marginBottom: 12,
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
        }}>
          Map theme
        </div>
        <Select<MapThemeSelectValue>
          size="small"
          value={mapThemeSelectValue}
          onChange={(v) =>
            onMapBasemapThemeOverrideChange(v === 'match' ? null : v)
          }
          popupMatchSelectWidth={false}
          prefix={(
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '3px 0',
                lineHeight: 0,
              }}
            >
              <MapThemeOptionIcon kind={mapThemeSelectValue} appTheme={theme} />
            </span>
          )}
          styles={{
            popup: { root: { minWidth: 200 } },
            prefix: {
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'center',
              paddingTop: 2,
              paddingBottom: 2,
            },
          }}
          style={{ width: '100%', marginBottom: 20 }}
          options={[
            { value: 'match', label: 'Same as app' },
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
          ]}
          optionRender={(option) => {
            const kind = option.value as MapThemeSelectValue;
            return (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <MapThemeOptionIcon kind={kind} appTheme={theme} />
                <span>{option.label}</span>
              </span>
            );
          }}
        />

        <div style={{
          fontSize: 14,
          fontWeight: 600,
          marginBottom: 12,
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
        }}>
          Map background
        </div>

        <Select
          size="small"
          value={mapBackgroundSelectValue}
          onChange={(v) => onMapStyleChange(v === 'default' ? null : v)}
          popupMatchSelectWidth={false}
          prefix={(
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '3px 0',
                lineHeight: 0,
              }}
            >
              <MapBackgroundSwatch
                kind={mapBackgroundSwatchKind}
                theme={theme}
                effectiveMapBasemapTheme={effectiveMapBasemapTheme}
              />
            </span>
          )}
          styles={{
            popup: { root: { minWidth: 200 } },
            prefix: {
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'center',
              paddingTop: 2,
              paddingBottom: 2,
            },
          }}
          style={{ width: '100%', marginBottom: 20 }}
          options={[
            { value: 'default', label: 'Default' },
            ...Object.entries(mapStyles).map(([key, style]) => ({
              value: key,
              label: style.name,
            })),
          ]}
          optionRender={(option) => {
            const v = String(option.value);
            const kind: MapBackgroundSwatchKind =
              v === 'default' ? 'default' : mapStyleKeyToSwatchKind(v);
            return (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <MapBackgroundSwatch
                  kind={kind}
                  theme={theme}
                  effectiveMapBasemapTheme={effectiveMapBasemapTheme}
                />
                <span>{option.label}</span>
              </span>
            );
          }}
        />

        {/* Layer toggles */}
        <div style={{ marginTop: 24 }}>
          <div style={{
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 12,
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
          }}>
            Layers
          </div>

          {/* Wind Arrows Toggle */}
          {observationsEnabled && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
              padding: '8px 12px',
              backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              borderRadius: 6,
            }}>
              <div style={{
                fontSize: 13,
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
              }}>
                Wind arrows
              </div>
              <button
                onClick={() => onWindArrowsToggle(!showWindArrows)}
                style={{
                  width: 44,
                  height: 24,
                  borderRadius: 12,
                  border: 'none',
                  backgroundColor: showWindArrows
                    ? '#1890ff'
                    : (theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.25)'),
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background-color 0.2s',
                }}
              >
                <div style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  position: 'absolute',
                  top: 3,
                  left: showWindArrows ? 23 : 3,
                  transition: 'left 0.2s',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                }} />
              </button>
            </div>
          )}

          {/* Storm Tracking Toggle - Only visible to root users (feature in development) */}
          {stormTrackingFeatureEnabled && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
              padding: '8px 12px',
              backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              borderRadius: 6,
            }}>
              <div style={{
                fontSize: 13,
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
              }}>
                Storm tracking
              </div>
              <button
                onClick={() => onStormTrackingToggle(!showStormTracking)}
                style={{
                  width: 44,
                  height: 24,
                  borderRadius: 12,
                  border: 'none',
                  backgroundColor: showStormTracking
                    ? '#1890ff'
                    : (theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.25)'),
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background-color 0.2s',
                }}
              >
                <div style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  position: 'absolute',
                  top: 3,
                  left: showStormTracking ? 23 : 3,
                  transition: 'left 0.2s',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                }} />
              </button>
            </div>
          )}

          {/* Rain Path Toggle */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
            padding: '8px 12px',
            backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
            borderRadius: 6,
          }}>
            <div style={{
              fontSize: 13,
              color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
            }}>
              Rain path
            </div>
            <button
              onClick={() => onRainPathToggle(!showRainPath)}
              style={{
                width: 44,
                height: 24,
                borderRadius: 12,
                border: 'none',
                backgroundColor: showRainPath
                  ? '#1890ff'
                  : (theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.25)'),
                cursor: 'pointer',
                position: 'relative',
                transition: 'background-color 0.2s',
              }}
            >
              <div style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                backgroundColor: '#fff',
                position: 'absolute',
                top: 3,
                left: showRainPath ? 23 : 3,
                transition: 'left 0.2s',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              }} />
            </button>
          </div>

          {/* Track Line Width Slider */}
          <div style={{
            marginBottom: 12,
            padding: '8px 12px',
            backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
            borderRadius: 6,
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}>
              <div style={{
                fontSize: 13,
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
              }}>
                Track line width
              </div>
              <span style={{
                fontSize: 12,
                fontWeight: 500,
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.55)',
                minWidth: 20,
                textAlign: 'right',
              }}>
                {trackLineWidth}px
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={10}
              step={1}
              value={trackLineWidth}
              onChange={(e) => onTrackLineWidthChange(Number(e.target.value))}
              style={{
                width: '100%',
                accentColor: '#1890ff',
                cursor: 'pointer',
              }}
            />
          </div>

          {/* Radius Circles Toggle */}
          <div style={{
            marginBottom: showRadiusCircles ? 8 : 12,
            padding: '8px 12px',
            backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
            borderRadius: 6,
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{
                fontSize: 13,
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
              }}>
                Radius circles
              </div>
              <button
                onClick={() => onRadiusCirclesToggle(!showRadiusCircles)}
                style={{
                  width: 44,
                  height: 24,
                  borderRadius: 12,
                  border: 'none',
                  backgroundColor: showRadiusCircles
                    ? '#1890ff'
                    : (theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.25)'),
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background-color 0.2s',
                }}
              >
                <div style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  position: 'absolute',
                  top: 3,
                  left: showRadiusCircles ? 23 : 3,
                  transition: 'left 0.2s',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                }} />
              </button>
            </div>

            {/* Radius circles settings - shown when toggle is on */}
            {showRadiusCircles && (
              <div style={{ marginTop: 12 }}>
                {/* Number of circles */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                }}>
                  <div style={{
                    fontSize: 12,
                    color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.55)',
                  }}>
                    Number of circles
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      onClick={() => onRadiusCircleCountChange(Math.max(1, radiusCircleCount - 1))}
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 4,
                        border: 'none',
                        backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
                        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      -
                    </button>
                    <span style={{
                      minWidth: 24,
                      textAlign: 'center',
                      fontSize: 13,
                      fontWeight: 500,
                      color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
                    }}>
                      {radiusCircleCount}
                    </span>
                    <button
                      onClick={() => onRadiusCircleCountChange(Math.min(10, radiusCircleCount + 1))}
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 4,
                        border: 'none',
                        backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
                        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Distance between circles */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <div style={{
                    fontSize: 12,
                    color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.55)',
                  }}>
                    Distance (km)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      onClick={() => onRadiusCircleDistanceChange(Math.max(1, radiusCircleDistance - 1))}
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 4,
                        border: 'none',
                        backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
                        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      -
                    </button>
                    <span style={{
                      minWidth: 24,
                      textAlign: 'center',
                      fontSize: 13,
                      fontWeight: 500,
                      color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
                    }}>
                      {radiusCircleDistance}
                    </span>
                    <button
                      onClick={() => onRadiusCircleDistanceChange(Math.min(50, radiusCircleDistance + 1))}
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 4,
                        border: 'none',
                        backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
                        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};
